import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Copy, Check, ArrowRight, Zap, MousePointerClick, Database, RefreshCw, Smartphone, ChevronLeft, ChevronRight, Volume2, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { PLANS, FAQS } from "../lib/constants";
import { useSeo, orgWebsiteSchema, faqSchema } from "../lib/useSeo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { IntroOfferBanner } from "../components/IntroOfferBanner";
import { VideoCarousel } from "../components/VideoCarousel";

const DEMO_QUERIES = ["write a complaint", "get old customers back", "create Facebook advert", "study for exam", "grow my business"];

const DemoSearch = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [typing, setTyping] = useState(true);
  const timer = useRef(null);

  useEffect(() => {
    let qi = 0, ci = 0, deleting = false;
    const tick = () => {
      if (!typing) return;
      const target = DEMO_QUERIES[qi];
      if (!deleting) {
        ci++;
        setQ(target.slice(0, ci));
        if (ci === target.length) { deleting = true; timer.current = setTimeout(tick, 1800); return; }
        timer.current = setTimeout(tick, 65);
      } else {
        ci--;
        setQ(target.slice(0, ci));
        if (ci === 0) { deleting = false; qi = (qi + 1) % DEMO_QUERIES.length; }
        timer.current = setTimeout(tick, 28);
      }
    };
    timer.current = setTimeout(tick, 600);
    return () => clearTimeout(timer.current);
  }, [typing]);

  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/demo-search", { params: { q } });
        setResults(data);
      } catch {}
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
        <input data-testid="demo-search-input" value={q}
          onFocus={() => { setTyping(false); clearTimeout(timer.current); }}
          onChange={(e) => { setTyping(false); setQ(e.target.value); }}
          placeholder="What do you want AI to help you do?"
          className="w-full bg-white border-2 border-slate-200 focus:border-emerald-500 rounded-2xl pl-12 pr-4 py-4 text-base sm:text-lg shadow-lg shadow-slate-900/5 focus:outline-none transition-colors" />
        {typing && <span className="type-caret absolute top-1/2 -translate-y-1/2 text-slate-800 text-lg" style={{ left: `${52 + q.length * 9.5}px` }}>|</span>}
      </div>
      <div className="mt-3 space-y-2 min-h-[120px]" data-testid="demo-search-results">
        {results.map((r, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3 rise" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="flex items-center gap-3 min-w-0">
              <span className="cmd-chip bg-slate-900 text-amber-400 font-bold px-2.5 py-1 rounded-lg text-sm shrink-0">{r.name}</span>
              <span className="text-xs text-slate-400 truncate hidden sm:block">{r.category}</span>
            </div>
            <Link to="/pricing" className="text-emerald-600 text-sm font-semibold shrink-0 hover:text-emerald-700">Unlock →</Link>
          </div>
        ))}
        {q && results.length === 0 && <p className="text-sm text-slate-400 text-center pt-4">Searching…</p>}
      </div>
    </div>
  );
};

const EXAMPLE_COMMANDS = ["/facebook-ad", "/meeting-minutes", "/complaint-letter", "/marketing-plan", "/youtube-thumbnail", "/business-growth", "/research-topic", "/customer-reactivation", "/rewrite-this", "/actasabusinesscoach"];

const STEPS = [
  { n: "1", title: "CREATE AN ACCOUNT", text: "Choose your membership and get instant access on mobile & desktop.", example: "From £27/year" },
  { n: "2", title: "SEARCH", text: "Type what you want AI to help you do.", example: "Get old customers back" },
  { n: "3", title: "FIND", text: "AI Shorthand shows you the command.", example: "/customer-reactivation" },
  { n: "4", title: "COPY", text: "Tap the Copy button.", example: "Copied!" },
  { n: "5", title: "ADD YOUR DETAILS", text: "Add a few quick details after the command.", example: "/customer-reactivation Beauty salon | customers who haven't booked for 6 months" },
  { n: "6", title: "USE IT", text: "Paste it into ChatGPT, Claude or Gemini.", example: "Done." },
];

const BENEFITS = [
  { icon: Zap, title: "INSTANT SEARCH", text: "Search normal phrases like advert, email, sales, admin, research or summary — and instantly find relevant commands." },
  { icon: MousePointerClick, title: "ONE-CLICK COPY", text: "Tap Copy and the command is on your clipboard, ready to paste into ChatGPT, Claude or Gemini." },
  { icon: Database, title: "HUNDREDS OF SHORTHAND COMMANDS", text: "Practical commands covering business, marketing, admin, research, study, sales, social media and much more." },
  { icon: RefreshCw, title: "CONTINUOUSLY UPDATED", text: "Unlike a static ebook, this is a living searchable database. New commands are added regularly." },
  { icon: Smartphone, title: "MOBILE FRIENDLY", text: "Works beautifully on smartphone, tablet, laptop and desktop — built for one-handed mobile use." },
];

const WHO = ["Small-business owners", "Managers", "Administrators", "Freelancers", "Consultants", "Students", "Entrepreneurs", "People new to AI"];

const PricingCards = ({ navigate, testPrefix = "pricing" }) => (
  <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
    {PLANS.map((p) => (
      <div key={p.key} data-testid={`${testPrefix}-card-${p.key}`}
        className={`relative bg-white rounded-2xl p-7 flex flex-col border-2 transition-all ${p.badge === "MOST POPULAR" ? "border-emerald-500 shadow-xl shadow-emerald-500/10 md:-translate-y-2" : "border-slate-200 shadow-sm"}`}>
        {p.badge && (
          <span className={`absolute -top-3.5 left-1/2 -translate-x-1/2 text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap ${p.badge === "MOST POPULAR" ? "bg-emerald-600 text-white" : "bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950"}`}>{p.badge}</span>
        )}
        <h3 className="font-display font-bold text-xl text-slate-900">{p.name}</h3>
        <img src="/assets/cert-badge.webp" alt="Earn your Certificate of Completion — XpressPrompts Starter Training"
          title="Includes the XpressPrompts™ Starter Training — earn your Certificate of Completion"
          draggable={false} data-testid={`${testPrefix}-cert-badge-${p.key}`}
          className="absolute top-4 right-4 w-16 h-16 drop-shadow-md" />
        <div className="mt-3 flex items-baseline gap-1">
          <span className="font-display text-4xl font-extrabold text-slate-900">{p.price}</span>
          <span className="text-slate-500 font-medium">{p.per}</span>
        </div>
        {p.trial && (
          <span className="inline-block mt-2 bg-emerald-50 text-emerald-700 text-xs font-bold px-3 py-1 rounded-full">
            1-DAY FREE TRIAL — £0 TODAY
          </span>
        )}
        {/* Note: Student and Pro plans no longer include a Stripe free trial.
             The card-less 24-hour taster runs via /start-trial instead. */}
        <p className="text-sm text-slate-500 mt-2">{p.blurb}</p>
        <ul className="mt-5 space-y-2.5 flex-1">
          {p.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
              <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>
        <button data-testid={`${testPrefix}-cta-${p.key}`} onClick={() => navigate(`/checkout/${p.key}`)}
          className={`mt-6 w-full font-bold text-sm px-4 py-3.5 rounded-xl active:scale-[0.98] transition-all ${p.badge === "MOST POPULAR" ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-600/25" : "bg-slate-900 hover:bg-slate-800 text-white"}`}>
          {p.cta}
        </button>
        <p className="text-xs text-slate-400 text-center mt-2.5">{p.renews}</p>
      </div>
    ))}
  </div>
);

export { PricingCards };

const TESTIMONIALS = [
  { name: "Sarah Mitchell", initials: "SM", role: "Salon Owner, Manchester", quote: "I used to stare at ChatGPT with no idea what to write. Now I search 'fill quiet appointment slots', copy the command, and I've got a promotion written before my next client sits down." },
  { name: "Dave Okafor", initials: "DO", role: "Electrician & Sole Trader, London", quote: "Quotes, chase-up emails, job adverts — the shortcuts do in seconds what used to eat my evenings. Paid for itself in the first week." },
  { name: "Priya Sharma", initials: "PS", role: "Marketing VA, Birmingham", quote: "The industry categories are the killer feature. I pick the client's trade, and the commands are already written for their world. My clients think I've hired a team." },
  { name: "Tom Bradley", initials: "TB", role: "Business Studies Student, Leeds", quote: "The Student plan got me through my dissertation. /summarise and /study-plan alone saved me dozens of hours — and I actually understand my notes now." },
  { name: "Claire Hughes", initials: "CH", role: "Life Coach, Bristol", quote: "I'm the least techy person I know. Search in plain English, copy, paste — that's it. My newsletters and session plans sound like me, just faster." },
  { name: "James Whitfield", initials: "JW", role: "Café Owner, Edinburgh", quote: "The 'Help me fill this in' button is genius. I type what I'm struggling to say, and it turns my ramble into the perfect prompt. Menus, socials, staff rotas — sorted." },
];

export default function Landing() {
  const navigate = useNavigate();
  const [countLabel, setCountLabel] = useState("hundreds of");
  const [videoActive, setVideoActive] = useState(false);
  const videoRef = useRef(null);

  const activateVideo = () => {
    const v = videoRef.current;
    if (!v || videoActive) return;
    setVideoActive(true);
    v.muted = false;
    v.loop = false;
    v.currentTime = 0;
    v.play().catch(() => {});
  };
  useSeo({
    title: "Stop Writing Essays to AI — Talk to ChatGPT in Shorthand",
    description: "Searchable database of hundreds of short-command shortcuts for ChatGPT, Claude and Gemini. Get marketing, admin, research and sales tasks done faster — from £27/year.",
    jsonLd: { "@context": "https://schema.org", "@graph": [...orgWebsiteSchema()["@graph"], faqSchema(FAQS)] },
  });

  useEffect(() => {
    api.post("/events", { type: "landing_visit" }).catch(() => {});
    api.get("/meta").then(({ data }) => setCountLabel(data.command_count_label)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="bg-[#0B0F0D] text-white relative overflow-hidden">
        <div className="hero-grain absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-28 relative">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="max-w-3xl">
            <p className="rise font-display text-[#D4AF37] font-bold text-sm sm:text-base tracking-[0.16em] uppercase">
              Universal Short-Command Prompt Dictionary™
            </p>
            <span className="rise mt-4 inline-flex items-center gap-2 bg-white/5 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E23D3D]" /> {countLabel} instant short-command shortcuts
            </span>
            <h1 className="rise rise-1 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] mt-6">
              Stop Writing Essays to AI.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-[#D4AF37] to-amber-500">Talk to ChatGPT in Shorthand.</span>
            </h1>
            <p className="rise rise-2 text-base md:text-lg text-gray-300 mt-6 max-w-2xl leading-relaxed">
              The searchable AI Shorthand database with {countLabel} instant short-command shortcuts for ChatGPT, Claude and Gemini. Get marketing, admin, research, sales and everyday tasks done faster — without learning prompt engineering.
            </p>
            <div className="rise rise-3 mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button data-testid="hero-cta-btn" onClick={() => navigate("/start-trial")}
                className="bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-500 hover:brightness-105 text-slate-950 font-bold px-7 py-4 rounded-xl shadow-lg shadow-amber-500/25 active:scale-[0.98] transition-all text-sm sm:text-base">
                GET FREE INSTANT ACCESS FOR 24 HOURS
              </button>
              <a href="#how-it-works" data-testid="hero-how-link" className="text-emerald-400 font-semibold flex items-center gap-1.5 hover:text-emerald-300 transition-colors">
                See How It Works <ArrowRight size={16} />
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <span data-testid="hero-trial-badge" className="inline-flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> 24-hour free trial — £0 today
              </span>
              <span data-testid="hero-no-card-pill" className="inline-flex items-center gap-1.5 bg-white/5 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full">
                <ShieldCheck size={12} /> No credit or debit card needed on registration
              </span>
              <p className="text-xs text-gray-500">Instant access on mobile &amp; desktop</p>
            </div>
          </div>
          <div className="rise rise-2 mt-10 lg:mt-0 flex justify-center">
            <div className="relative w-full max-w-[300px] sm:max-w-[330px] rounded-[2rem] overflow-hidden border-2 border-[#D4AF37]/50 shadow-2xl shadow-black/50 bg-black cursor-pointer" onClick={activateVideo}>
              <video
                ref={videoRef}
                data-testid="hero-video"
                src="/videos/hero-xpressprompts.mp4"
                autoPlay
                muted
                loop
                playsInline
                controls={videoActive}
                preload="auto"
                className="w-full aspect-[9/16] object-cover"
              />
              {!videoActive && (
                <button type="button" data-testid="hero-video-sound-btn" onClick={activateVideo}
                  className="absolute bottom-4 right-1/2 translate-x-1/2 flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-[#D4AF37]/50 text-[#D4AF37] text-xs font-bold px-4 py-2.5 rounded-full hover:bg-black/80 transition-colors whitespace-nowrap">
                  <Volume2 size={14} /> Tap for sound
                </button>
              )}
            </div>
          </div>
          </div>
        </div>
      </section>

      {/* DEMO SEARCH */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs sm:text-sm text-emerald-700 uppercase tracking-wider font-bold">Try it — live demo</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Google for What to Ask AI</h2>
            <p className="text-slate-500 mt-3 max-w-xl mx-auto text-sm sm:text-base">Search it. Find it. Copy it. Add your details. Use it.</p>
          </div>
          <DemoSearch />
        </div>
      </section>

      {/* OLD WAY VS NEW WAY */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center">Why write a long prompt when a short command gets you started?</h2>
          <div className="max-w-2xl mx-auto mt-10">
            <button onClick={() => navigate("/pricing")} data-testid="old-vs-shorthand-link"
              className="block w-full rounded-2xl overflow-hidden border border-slate-200 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer">
              <img
                src="https://customer-assets-m6fa6gv7.emergentagent.net/job_ai-shorthand/artifacts/zl2ncbbr_How%20to%20use%20ai%20prompt%20shortcut%20commands%202.png"
                alt="The Old Way vs The Shorthand Way — same job, less explaining. Instead of writing a long paragraph to explain the job, just name it: /customer-reactivation. Stop describing the job. Name the job. Try it for 24 hrs free."
                className="w-full h-auto"
                loading="lazy"
                data-testid="old-vs-shorthand-image"
              />
            </button>
          </div>
          <div className="mt-14 text-center">
            <p className="text-xs sm:text-sm text-emerald-700 uppercase tracking-wider font-bold">Meet the Prompt Queen</p>
            <p className="text-slate-500 mt-2 max-w-xl mx-auto text-sm sm:text-base">Her videos offer tips and advice on how to effectively use Short Commands.</p>
            <div className="max-w-3xl mx-auto mt-6">
              <button onClick={() => navigate("/prompt-queen-tips")} data-testid="prompt-queen-home-banner"
                className="block w-full rounded-2xl overflow-hidden bg-black shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer">
                <img src="/assets/prompt-queen-banner.webp"
                  alt="Prompt Queen — AI Advice & Tips. Watch her videos for tips on using Short Commands effectively."
                  className="w-full h-auto" loading="lazy" />
              </button>
            </div>

            <a
              href="https://www.qualiquiz.app/quiz/discover-your-ai-workforce-readiness-assessment-report"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-readiness-banner-link"
              className="block max-w-3xl mx-auto mt-8 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
            >
              <img
                src="/assets/ai-workforce-readiness-banner.webp"
                alt="Discover Your AI Workforce Readiness Score — take our free 2-minute assessment and receive a personalised snapshot of where your organisation could improve AI adoption and everyday execution."
                className="w-full h-auto block"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      </section>

      {/* EXPLAINER VIDEO CAROUSEL */}
      <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="explainer-video-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center">
            <p className="text-xs sm:text-sm text-emerald-700 uppercase tracking-wider font-bold">Watch &amp; learn</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">See AI Shorthand in action</h2>
            <p className="mt-3 text-sm sm:text-base text-gray-600">Swipe through short explainers, walkthroughs and tips.</p>
          </div>
          <div className="mt-8">
            <VideoCarousel />
          </div>
        </div>
      </section>

      {/* WELCOME */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#0B0F0D] text-white" data-testid="welcome-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center">
            <span className="inline-block bg-[#D4AF37]/15 text-[#D4AF37] text-lg sm:text-xl font-bold uppercase tracking-widest px-6 py-2.5 rounded-full">Why You Need This</span>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-5 text-center text-[#D4AF37]">You've just found the shortcut everyone else wishes they had</h2>
          <div className="mt-6 space-y-4 text-gray-300 leading-relaxed">
            <p>Let's be honest: everyone says AI will save you hours… but nobody tells you <strong className="text-white">what to actually type</strong>. So you sit there, staring at a blank ChatGPT box, typing long rambling requests and getting average answers back.</p>
            <p className="text-white font-semibold">That ends today.</p>
            <p>The <strong className="text-[#D4AF37]">Universal Short-Command Prompt Dictionary™</strong> is exactly what it sounds like — a searchable dictionary of hundreds of short commands that tell ChatGPT, Claude or Gemini <em>precisely</em> what you want, in seconds. No prompt engineering. No tech skills. No guesswork.</p>
            <p>Just search what you need in plain English — "write an email", "get more customers", "plan my week" — and we hand you the exact short command, with ready-made examples. Copy it, add your details, paste it into your AI. Done.</p>
            <p>Whatever you do, we speak your language: hairdresser, tradesperson, teacher, shop owner, coach — your shortcuts are already organised for 32 industries. Think of us as <strong className="text-white">Google for what to ask AI</strong>.</p>
            <p className="text-emerald-400 font-bold">Stop typing essays. Start typing shortcuts.</p>
          </div>
          <Link to="/blog/welcome-you-ve-just-found-the-shortcut-everyone-else-wishes-they-had" data-testid="welcome-blog-link"
            className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-[#D4AF37] hover:text-amber-300 transition-colors">
            Read the full welcome post <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF7]">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-4xl mx-auto mb-12">
            <div className="relative rounded-2xl overflow-hidden border-2 border-[#D4AF37] shadow-xl shadow-amber-900/10 aspect-video bg-black" data-testid="how-it-works-video">
              <iframe
                src="https://www.youtube.com/embed/5l_PQZbztRQ"
                title="How AI Prompt Shortcuts works"
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          </div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center">How It Works</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`how-step-${s.n}`}>
                <div className="w-9 h-9 rounded-full bg-[#0B0F0D] text-[#D4AF37] font-display font-bold flex items-center justify-center">{s.n}</div>
                <h3 className="font-display font-bold text-sm mt-3 tracking-wide">{s.title}</h3>
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{s.text}</p>
                <p className="text-xs text-emerald-700 font-semibold mt-2.5 bg-emerald-50 rounded-lg px-2.5 py-1.5 break-words">{s.example}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO OFFER */}
      <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-[#FAFAF7]">
        <IntroOfferBanner testPrefix="home" />
      </section>

      {/* VIDEO — WHAT COULD YOU ASK AI */}
      <section className="pb-4 lg:pb-8 px-4 sm:px-6 lg:px-8 bg-[#FAFAF7]">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden border-2 border-[#D4AF37] shadow-xl shadow-amber-900/10 aspect-video bg-black" data-testid="ask-ai-video">
            <iframe
              src="https://www.youtube.com/embed/ZEeVsgmrxu4"
              title="What could you ask AI to do?"
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* EXAMPLES */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#0B0F0D] text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">What could you ask AI to do?</h2>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            {EXAMPLE_COMMANDS.map((c) => (
              <span key={c} className="cmd-chip bg-white/5 border border-white/10 text-amber-400 font-bold px-4 py-2.5 rounded-xl text-sm hover:border-[#D4AF37]/50 hover:bg-white/10 transition-all cursor-default">{c}</span>
            ))}
          </div>
          <p className="font-display text-[#D4AF37] font-bold text-lg mt-8 tracking-wide">And Hundreds More</p>
          <div className="mt-10 max-w-2xl mx-auto text-gray-300 text-sm sm:text-base leading-relaxed">
            <p className="font-display font-bold text-white text-lg mb-3">STOP JUST SEARCHING WITH AI</p>
            <p>AI can help you write, plan, create, analyse, research, promote, organise, sell, learn, brainstorm and advise. You simply need to know what to ask it to do.</p>
            <p className="text-emerald-400 font-semibold mt-2">AI Shorthand gives you the shortcut.</p>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center">Everything you need to put AI to work</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {BENEFITS.map((b) => (
              <div key={b.title} className="border border-slate-200 rounded-2xl p-6 hover:border-emerald-500/40 hover:shadow-md transition-all">
                <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><b.icon size={20} /></div>
                <h3 className="font-display font-bold text-sm mt-4 tracking-wide">{b.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{b.text}</p>
              </div>
            ))}
            <div className="bg-[#0B0F0D] rounded-2xl p-6 text-white flex flex-col justify-between">
              <div>
                <h3 className="font-display font-bold text-sm tracking-wide text-[#D4AF37]">BONUS: QUICK-COMMAND CHEAT SHEET</h3>
                <p className="text-sm text-gray-300 mt-2 leading-relaxed">Every paid member gets the downloadable Quick-Command Cheat Sheet for Admin Professionals, Students, Managers, Small Businesses &amp; Entrepreneurs.</p>
              </div>
              <button onClick={() => navigate("/pricing")} className="mt-4 text-emerald-400 font-semibold text-sm flex items-center gap-1 hover:text-emerald-300">Included with every plan <ChevronRight size={15} /></button>
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF7]">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/pricing" data-testid="home-24hr-banner-link" className="block mb-10">
            <img src="/assets/home-24hr-banner.webp"
              alt="XpressPrompts — Universal Short-Command Prompt Dictionary. Talk to AI in shorthand. FREE Get Instant Access For 24hrs. No credit or debit card required initially. www.xpressprompts.app"
              data-testid="home-24hr-banner"
              className="w-full max-w-3xl mx-auto rounded-2xl shadow-xl hover:scale-[1.02] transition-transform" />
          </Link>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Built for people who don't want to learn “prompt engineering”</h2>
          <div className="flex flex-wrap justify-center gap-2.5 mt-8">
            {WHO.map((w) => (
              <span key={w} className="bg-white border border-slate-200 text-slate-700 text-sm font-medium px-4 py-2 rounded-full">{w}</span>
            ))}
          </div>
          <p className="text-slate-500 mt-8 text-sm sm:text-base">And anyone who has ever opened ChatGPT and thought:</p>
          <p className="font-display text-xl sm:text-2xl font-bold text-slate-900 mt-2">“What am I supposed to type?”</p>
          <div className="relative mt-10 rounded-2xl overflow-hidden border-2 border-[#D4AF37] shadow-xl shadow-amber-900/10 aspect-video bg-black"
            data-testid="youtube-howto-video">
            <iframe
              src="https://www.youtube.com/embed/90wHg4fOaXc"
              title="How to use AI Prompt Shortcuts"
              className="absolute inset-0 w-full h-full"
              loading="lazy"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* TEAMS & ENTERPRISE BANNER */}
      <section className="py-10 px-4 sm:px-6 lg:px-8" data-testid="teams-banner-section">
        <Link to="/enterprise" data-testid="home-teams-banner-link" className="block max-w-5xl mx-auto">
          <img src="/assets/teams-enterprise-banner.webp"
            alt="AI Workforce Enablement — Teams & Enterprise. Enable adoption. Empower staff. Explore web.aipromptshortcuts.app/enterprise"
            data-testid="home-teams-banner" className="w-full rounded-2xl shadow-lg hover:scale-[1.01] transition-transform" />
        </Link>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#0B0F0D] text-white" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs sm:text-sm text-[#D4AF37] uppercase tracking-wider font-bold text-center">Member stories</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center mt-2">Loved by people who just want the job done</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {TESTIMONIALS.map((t) => (
              <figure key={t.name} data-testid={`testimonial-${t.name.split(" ")[0].toLowerCase()}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col hover:border-[#D4AF37]/40 transition-colors">
                <div className="flex gap-0.5 text-[#D4AF37]" aria-label="5 out of 5 stars">
                  {"★★★★★".split("").map((s, i) => <span key={i} className="text-sm">{s}</span>)}
                </div>
                <blockquote className="text-sm text-gray-300 leading-relaxed mt-3 flex-1">“{t.quote}”</blockquote>
                <figcaption className="flex items-center gap-3 mt-5">
                  <span className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-yellow-600 text-slate-950 font-bold text-sm flex items-center justify-center shrink-0">{t.initials}</span>
                  <span>
                    <span className="block text-sm font-bold text-white">{t.name}</span>
                    <span className="block text-xs text-gray-400">{t.role}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <button onClick={() => navigate("/checkout/pro_intro")} data-testid="offer-poster-link"
            className="block max-w-md mx-auto mb-12 rounded-2xl overflow-hidden border-2 border-[#D4AF37] shadow-xl shadow-amber-900/15 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/2b4a952f-da01-4105-8d06-616013b8376a/images/9116176610c1111f5ea62ca0f53327a972ab3675368ac3f28fc0594da31fd19c.jpeg"
              alt="New Customer Offer — 1 Month Full Access for £4.99. Join now."
              className="w-full h-auto" loading="lazy" />
          </button>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center mb-14">Choose your membership</h2>
          <PricingCards navigate={navigate} testPrefix="landing-pricing" />
          <Link to="/start-trial" data-testid="landing-trial-link" className="block max-w-3xl mx-auto mt-12">
            <img src="/assets/trial-24hr-card.webp"
              alt="Try free for 24 hours — no card needed. Full Pro access to Images & Visual Content, Marketing & Business Promotion and Admin & Office Productivity. Start 24-hour pass."
              data-testid="landing-trial-banner"
              draggable={false}
              className="w-full rounded-2xl shadow-lg hover:scale-[1.02] transition-transform" />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#FAFAF7]">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-center mb-10">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="bg-white rounded-2xl border border-slate-200 px-5" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`q${i}`}>
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 text-sm leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#0B0F0D] text-white text-center">
        <div className="max-w-4xl mx-auto mb-12">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/2b4a952f-da01-4105-8d06-616013b8376a/images/eeb5ffb66e6b2641aa0b11d04e7a8ee627e33518bb9dd2a0ea9aa2bf6f6d0e28.jpeg"
            alt="Stop writing essays to get ChatGPT results — use short commands instead. Hundreds of instant shortcuts, searchable in seconds, works with ChatGPT, Claude and Gemini."
            className="w-full rounded-2xl shadow-2xl shadow-amber-500/10 border border-amber-500/20"
            loading="lazy"
            data-testid="stop-writing-essays-image"
          />
        </div>
        <div className="max-w-2xl mx-auto">
          <p className="text-xs sm:text-sm text-[#D4AF37] uppercase tracking-widest font-bold">No coding. No long prompts. No technical jargon.</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-4 leading-tight">
            Search it. Copy it.<br />Add your details. Use it.
          </h2>
          <p className="text-gray-300 mt-4">Stop wondering what to ask AI. Start putting AI to work.</p>
          <button data-testid="final-cta-btn" onClick={() => navigate("/start-trial")}
            className="mt-8 bg-gradient-to-r from-amber-500 via-amber-400 to-yellow-500 hover:brightness-105 text-slate-950 font-bold px-8 py-4 rounded-xl shadow-lg shadow-amber-500/25 active:scale-[0.98] transition-all">
            GET FREE INSTANT ACCESS FOR 24 HOURS
          </button>
          <div className="mt-4 flex flex-col items-center gap-2.5">
            <span data-testid="final-cta-trial-badge" className="inline-flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> 24-hour free trial — £0 today
            </span>
            <p className="text-xs text-gray-500">Student £27/year • Pro £47/year • Lifetime £99</p>
          </div>
        </div>
      </section>

      {/* BECOME A PARTNER */}
      <section className="py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-[#0B0F0D]">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate("/become-a-partner")} data-testid="partner-banner-link"
            className="block w-full rounded-2xl overflow-hidden border border-[#D4AF37]/40 shadow-xl shadow-amber-900/15 hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/2b4a952f-da01-4105-8d06-616013b8376a/images/d1d82ddaa88579e0036ab843de44d3b6bae1230c3cafc0068f67eec7354383fe.jpeg"
              alt="Become a Partner — Partner & Collaborator Network. Grow visibility, share audiences. Reciprocal logo links, no partnership fee, Lifetime Partner Access. Apply now."
              className="w-full h-auto" loading="lazy" />
          </button>
        </div>
      </section>
    </div>
  );
}
